/**
 * Contact-state React Query key roots (U-C — centralized cache invalidation).
 *
 * Split into their own tiny module (not colocated with `contact/hooks.ts`,
 * which is where they're actually consumed as query keys) so that
 * `EnrollQueueContext.tsx` — which must invalidate these keys from every
 * queue-mutating path (add/patch/remove/send-settle) — can import just the
 * key roots without importing `contact/hooks.ts` itself. Importing the hooks
 * module from the queue context would risk a cycle (contact/hooks.ts has no
 * reason to import the queue context today, but nothing prevents it from
 * growing one later) and would pull in every other contact-state hook's
 * dependencies for the sake of two array literals.
 *
 * `hooks.ts` spreads these roots into its queryKey arrays (`[...FLOW_
 * CONTACT_STATE_KEY, idsKey]`, `[...FLOW_CONTACT_STATE_DETAIL_KEY,
 * contactId]`) so the root segment can never drift between the query
 * definition and any invalidation call site — both read the same constant.
 */

export const FLOW_CONTACT_STATE_KEY = ['flow-contact-state'] as const;
export const FLOW_CONTACT_STATE_DETAIL_KEY = ['flow-contact-state-detail'] as const;

/**
 * `useFlowContactTimeline`'s query key root (`GET /api/flow/contacts/:id/timeline`).
 * Lives here so the refresh-on-read path can invalidate it (the timeline now
 * carries campaign member notes) without importing the hooks module.
 */
export const FLOW_CONTACT_TIMELINE_KEY = ['flow-contact-timeline'] as const;

/**
 * `useFlowContactSignals`'s query key root (plan
 * `2026-09-22-contact-actions-intelligence-implementation-plan.md` §2b item
 * 3) — the Actions tab's per-contact `terra_signals` read
 * (`GET /api/flow/contacts/:id/signals`). Lives here, not `contact/hooks.ts`,
 * for the same reason `FLOW_CONTACT_STATE_KEY` does: `EnrollQueueContext.tsx`
 * invalidates it from every queue-mutating path (a fresh queue item is a new
 * Terra-queue-arm row, so the Actions tab must refresh without a reload) and
 * must not import the hooks module itself to do so.
 */
export const FLOW_CONTACT_SIGNALS_KEY = ['flow-contact-signals'] as const;

/**
 * `useFlowAccountSignals`'s query key root (plan 2026-09-23-001 U6 / R17) —
 * the per-account `terra_signals` read (`GET /api/flow/accounts/:id/signals`)
 * whose rows now merge into `AccountTimeline`'s accordion as action rows.
 *
 * Same reason for living here rather than in `account/hooks.ts` as
 * `FLOW_CONTACT_SIGNALS_KEY` above: a fresh queue item is a new
 * Terra-queue-arm row for BOTH the contact and its account, so
 * `EnrollQueueContext.tsx` invalidates this root too and must import the key,
 * not the hooks module.
 */
export const FLOW_ACCOUNT_SIGNALS_KEY = ['flow-account-signals'] as const;
